// ======================================================================
// File: src/modules/ipe/dashboard/IPEDashboard.jsx
// Purpose:
// Main IPE Executive Dashboard
// CRM | IPE | Procurement | Inventory | Finance |
// Analytics | FMS | Transport
// ======================================================================

import React, { useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";

import CRMDashboard from "./crm/CRMDashboard";
import IPEExecutionDashboard from "./ipe/IPEExecutionDashboard";
import ProcurementDashboard from "./procurement/ProcurementDashboard";
import InventoryDashboard from "./inventory/InventoryDashboard";
import FinanceDashboard from "./finance/FinanceDashboard";
import AnalyticsDashboard from "./analytics/AnalyticsDashboard";
import FMSDashboard from "./fms/FMSDashboard";
import TransportDashboard from "./transport/TransportDashboard";

/*
======================================================================
TAB CONFIGURATION
======================================================================
*/

const DASHBOARD_TABS = [
  {
    key: "crm",
    label: "CRM",
    tabPath: "/ipe/dashboard/crm",
    modulePath: "/ipe/dashboard/crm",
  },
  {
    key: "ipe",
    label: "IPE",
    tabPath: "/ipe/dashboard/ipe",
    modulePath: "/ipe/dashboard/ipe",
  },
  {
    key: "procurement",
    label: "Procurement",
    tabPath: "/ipe/dashboard/procurement",
    modulePath: "/ipe/dashboard/procurement",
  },
  {
    key: "inventory",
    label: "Inventory",
    tabPath: "/ipe/dashboard/inventory",
    modulePath: "/ipe/dashboard/inventory",
  },
  {
    key: "finance",
    label: "Finance",
    tabPath: "/ipe/dashboard/finance",
    modulePath: "/ipe/dashboard/finance",
  },
  {
    key: "analytics",
    label: "Analytics",
    tabPath: "/ipe/dashboard/analytics",
    modulePath: "/ipe/dashboard/analytics",
  },
  {
    key: "fms",
    label: "FMS",
    tabPath: "/ipe/dashboard/fms",
    modulePath: "/ipe/dashboard/fms",
  },
  {
    key: "transport",
    label: "Transport",
    tabPath: "/ipe/dashboard/transport",
    modulePath: "/ipe/dashboard/transport",
  },
];

export default function IPEDashboard() {
  const navigate = useNavigate();
  const location = useLocation();

  // ============================================================
  // AUTO OPEN LAST TAB
  // ============================================================

  useEffect(() => {
    const currentPath = location.pathname;

    if (
      currentPath === "/ipe/dashboard" ||
      currentPath === "/ipe/dashboard/"
    ) {
      const lastTab = localStorage.getItem(
        "ipe_active_dashboard"
      );

      navigate(
        lastTab || "/ipe/dashboard/crm",
        { replace: true }
      );
    }
  }, [location.pathname, navigate]);

  // ============================================================
  // ACTIVE TAB
  // ============================================================

  const isActive = (tab) =>
    location.pathname.startsWith(tab.tabPath);

  // ============================================================
  // TAB CLICK
  // ============================================================

  const openTab = (tab) => {
    localStorage.setItem(
      "ipe_active_dashboard",
      tab.modulePath
    );

    navigate(tab.modulePath);
  };

  return (
    <div>

      {/* ======================================================
          INLINE STYLES
      ====================================================== */}

      <style>{`

        /* ==================================================
           TAB WRAPPER
        ================================================== */

        .ipe-dashboard-tabs {
          background: #e5e7eb;
          padding: 12px 12px 0;
          border-radius: 12px;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        /* ==================================================
           TAB ROW
        ================================================== */

        .ipe-tab-container {
          display: flex;
          gap: 6px;
          align-items: flex-end;
          flex-wrap: wrap;
        }

        /* ==================================================
           TAB
        ================================================== */

        .ipe-tab {
          position: relative;
          border: none;
          background: #d1d5db;
          padding: 10px 22px;
          border-radius: 12px 12px 0 0;
          font-weight: 500;
          color: #374151;
          cursor: pointer;
          transition: all 0.25s ease;
        }

        .ipe-tab:hover {
          background: #cbd5e1;
        }

        /* ==================================================
           ACTIVE TAB
        ================================================== */

        .ipe-tab.active {
          background: linear-gradient(
            90deg,
            #6366f1,
            #4f46e5
          );
          color: #ffffff;
          z-index: 2;
        }

        .ipe-tab.active::after {
          content: "";
          position: absolute;
          right: -14px;
          bottom: 0;
          width: 28px;
          height: 28px;
          background: #e5e7eb;
          border-bottom-left-radius: 20px;
        }

        /* ==================================================
           CONTENT AREA
        ================================================== */

        .ipe-content-area {
          background: #ffffff;
          padding: 16px;
          border-radius: 0 12px 12px 12px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.06);
          min-height: 700px;
        }

      `}</style>

      {/* ======================================================
          TAB BAR
      ====================================================== */}

      <div className="ipe-dashboard-tabs">
        <div className="ipe-tab-container">

          {DASHBOARD_TABS.map((tab) => (
            <button
              key={tab.key}
              className={`ipe-tab ${
                isActive(tab) ? "active" : ""
              }`}
              onClick={() => openTab(tab)}
            >
              {tab.label}
            </button>
          ))}

        </div>
      </div>

      {/* ======================================================
          CONTENT
      ====================================================== */}

      <div className="ipe-content-area">

        <Routes>

          {/* CRM */}
          <Route
            path="crm/*"
            element={<CRMDashboard />}
          />

          {/* IPE */}
          <Route
            path="ipe"
            element={<IPEExecutionDashboard />}
          />

          {/* PROCUREMENT */}
          <Route
            path="procurement"
            element={<ProcurementDashboard />}
          />

          {/* INVENTORY */}
          <Route
            path="inventory"
            element={<InventoryDashboard />}
          />

          {/* FINANCE */}
          <Route
            path="finance"
            element={<FinanceDashboard />}
          />

          {/* ANALYTICS */}
          <Route
            path="analytics"
            element={<AnalyticsDashboard />}
          />

          {/* FMS */}
          <Route
            path="fms"
            element={<FMSDashboard />}
          />

          {/* TRANSPORT */}
          <Route
            path="transport"
            element={<TransportDashboard />}
          />

          {/* DEFAULT */}
          <Route
            path="*"
            element={
              <Navigate
                to="/ipe/dashboard/crm"
                replace
              />
            }
          />

        </Routes>

      </div>

    </div>
  );
}